import { QueryClient } from '@tanstack/react-query';
import type { PaginationParams } from '@geely/shared/types';
import type { AppointmentQueryParams } from './api/appointments.api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Query keys for cache management
export const queryKeys = {
  // Users
  users: ['users'] as const,
  usersPaginated: (params: PaginationParams) => ['users', 'paginated', params] as const,
  user: (id: string) => ['users', id] as const,

  // Stores
  stores: ['stores'] as const,
  storesPaginated: (params: PaginationParams) => ['stores', 'paginated', params] as const,
  store: (id: string) => ['stores', id] as const,

  // Appointments
  appointments: (params?: { storeId?: string; date?: string }) =>
    ['appointments', params] as const,
  appointmentsPaginated: (params: AppointmentQueryParams & PaginationParams) =>
    ['appointments', 'paginated', params] as const,
  appointment: (id: string) => ['appointments', 'detail', id] as const,

  // Operating Hours
  operatingHours: (storeId: string) => ['operatingHours', storeId] as const,

  // Weekly Operating Hours
  weeklyOperatingHours: (storeId: string) => ['weeklyOperatingHours', storeId] as const,

  // Special Date Operating Hours
  specialDateOperatingHours: (storeId: string) => ['specialDateOperatingHours', storeId] as const,
  specialDateOperatingHoursPaginated: (storeId: string, params: PaginationParams) =>
    ['specialDateOperatingHours', storeId, 'paginated', params] as const,

  // Models
  models: ['models'] as const,
  modelsPaginated: (params: PaginationParams) => ['models', 'paginated', params] as const,
  model: (id: string) => ['models', id] as const,

  // Versions
  versions: (modelId?: string) => ['versions', modelId] as const,
  versionsPaginated: (params: { modelId?: string } & PaginationParams) =>
    ['versions', 'paginated', params] as const,
  version: (id: string) => ['versions', 'detail', id] as const,
};
